import {Flex, Grid, Paper, Text} from '@mantine/core';
import React from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  CH3,
  FC,
  getSingleDomainModule,
  getTwoDomainModule,
  HETERO_CH3,
  HETERO_FC,
  HETERO_IGG,
  HINGE,
  IGG,
  MONO_CH3,
  MONO_FC,
  SCFV,
  SCFAB,
  FAB,
  FUSION,
} from 'antibodies/format/help-modal/constants';
import {VeritasHelpModalSection, VeritasHelpModalSectionTitle} from 'antibodies/format/help-modal/shared';
import Antibody from 'antibodies/visualization/Antibody';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';

interface ModuleVisualProps {
  label: string;
  format: AntibodyFormat;
  showLabels?: boolean;
}

const MODULE_VISUAL_SIZE = 100;

function ModuleVisual({label, format, showLabels = false}: ModuleVisualProps): JSX.Element {
  return (
    <Flex direction="column" align="center" gap="xs">
      <Flex align="center" justify="center">
        <Paper w={`${MODULE_VISUAL_SIZE}px`} h={`${MODULE_VISUAL_SIZE}px`} bg="gray.2" radius="md">
          <Antibody format={format} showLabels={showLabels} />
        </Paper>
      </Flex>
      <Text fz="sm" c="gray.6">
        {label}
      </Text>
    </Flex>
  );
}

interface DescriptionItemProps {
  name: string;
  description: string;
}

function DescriptionItem({name, description}: DescriptionItemProps): JSX.Element {
  return (
    <Flex gap="xs">
      <Text span={true} fw="bold">
        {name}:
      </Text>
      <Text span={true}>{description}</Text>
    </Flex>
  );
}

function ModuleVisualContainer({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Paper bg="gray.0" p="lg" mb="md" radius="lg" withBorder={true}>
      <Flex gap="lg" wrap="wrap">
        {children}
      </Flex>
    </Paper>
  );
}

export function ComponentsTab(): JSX.Element {
  return (
    <div>
      <VeritasHelpModalSection>
        <VeritasHelpModalSectionTitle>Basic modules</VeritasHelpModalSectionTitle>
        <ModuleVisualContainer>
          <ModuleVisual
            label="VHH"
            format={getSingleDomainModule(AntibodyDomainType.VHH)}
            showLabels={true}
          />
          <ModuleVisual label="VH" format={getSingleDomainModule(AntibodyDomainType.VH)} showLabels={true} />
          <ModuleVisual label="VL" format={getSingleDomainModule(AntibodyDomainType.VL)} showLabels={true} />
          <ModuleVisual
            label="CH1"
            format={getSingleDomainModule(AntibodyDomainType.CH1)}
            showLabels={true}
          />
          <ModuleVisual
            label="CH2"
            format={getSingleDomainModule(AntibodyDomainType.CH2)}
            showLabels={true}
          />
          <ModuleVisual
            label="CH3"
            format={getSingleDomainModule(AntibodyDomainType.CH3)}
            showLabels={true}
          />
          <ModuleVisual label="CL" format={getSingleDomainModule(AntibodyDomainType.CL)} showLabels={true} />
        </ModuleVisualContainer>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="VHH/dAb" description="Variable heavy single domain" />
            <DescriptionItem name="VH" description="Variable heavy domain" />
            <DescriptionItem name="VL" description="Variable light domain" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="CH1, CH2, CH3" description="Constant heavy domains" />
            <DescriptionItem name="CL" description="Constant light domain" />
          </Grid.Col>
        </Grid>
      </VeritasHelpModalSection>

      <VeritasHelpModalSection>
        <VeritasHelpModalSectionTitle>Complex modules</VeritasHelpModalSectionTitle>
        <ModuleVisualContainer>
          <ModuleVisual
            label="Fd"
            format={getTwoDomainModule(AntibodyDomainType.VH, AntibodyDomainType.CH1)}
            showLabels={true}
          />
          <ModuleVisual
            label="LC"
            format={getTwoDomainModule(AntibodyDomainType.VL, AntibodyDomainType.CL)}
            showLabels={true}
          />
          <ModuleVisual label="Fab" format={FAB} />
          <ModuleVisual label="scFv" format={SCFV} />
          <ModuleVisual label="scFab" format={SCFAB} />
          <ModuleVisual label="F" format={FUSION} />
        </ModuleVisualContainer>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="Fd" description="VH-CH1" />
            <DescriptionItem name="LC" description="VL-CL" />
            <DescriptionItem name="Fab" description="Fragment antigen-binding" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="scFv" description="Single-chain variable fragment" />
            <DescriptionItem name="scFab" description="Single-chain Fab" />
            <DescriptionItem name="F" description="Fusion domain" />
          </Grid.Col>
        </Grid>
      </VeritasHelpModalSection>

      <VeritasHelpModalSection>
        <VeritasHelpModalSectionTitle>Dimerization centers</VeritasHelpModalSectionTitle>
        <ModuleVisualContainer>
          <ModuleVisual label="IgG" format={IGG} />
          <ModuleVisual label="heteroIgG" format={HETERO_IGG} />
          <ModuleVisual label="Fc" format={FC} />
          <ModuleVisual label="heteroFc" format={HETERO_FC} />
          <ModuleVisual label="Fab" format={FAB} />
          <ModuleVisual label="CH3" format={CH3} />
          <ModuleVisual label="heteroCH3" format={HETERO_CH3} />
          <ModuleVisual label="hinge" format={HINGE} />
          {/* Hinge looks the same as heterohinge! */}
          <ModuleVisual label="heteroHinge" format={HINGE} />
        </ModuleVisualContainer>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="IgG" description="Full immunoglobulin" />
            <DescriptionItem name="heteroIgG" description="Asymmetric IgG with different chains" />
            <DescriptionItem name="Fc" description="Symmetric Fragment crystallization region" />
            <DescriptionItem name="heteroFc" description="Asymmetric Fc" />
            <DescriptionItem name="Fab" description="Fragment antigen-binding" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="CH3" description="Symmetric CH3 with hinge" />
            <DescriptionItem name="heteroCH3" description="Asymmetric CH3 with hinge" />
            <DescriptionItem name="hinge" description="Hinge only center" />
            <DescriptionItem name="heteroHinge" description="Asymmetric hinge only center" />
          </Grid.Col>
        </Grid>
      </VeritasHelpModalSection>

      <VeritasHelpModalSection>
        <VeritasHelpModalSectionTitle>Monomerization centers</VeritasHelpModalSectionTitle>
        <ModuleVisualContainer>
          <ModuleVisual label="monoFc" format={MONO_FC} />
          <ModuleVisual label="monoCH3" format={MONO_CH3} />
          <ModuleVisual label="scFv" format={SCFV} />
          <ModuleVisual label="scFab" format={SCFAB} />
          <ModuleVisual label="VHH" format={getSingleDomainModule(AntibodyDomainType.VHH)} />
        </ModuleVisualContainer>
        <Grid gutter="md">
          <Grid.Col span={6}>
            <DescriptionItem name="monoFc" description="Single Fc" />
            <DescriptionItem name="monoCH3" description="Single CH3" />
            <DescriptionItem name="scFv" description="Single-chain variable fragment" />
          </Grid.Col>
          <Grid.Col span={6}>
            <DescriptionItem name="scFab" description="Single-chain Fab" />
            <DescriptionItem name="VHH" description="Variable heavy single domain" />
          </Grid.Col>
        </Grid>
      </VeritasHelpModalSection>
    </div>
  );
}
